
export const introText = `June Choi (b. 2001) is a visual artist and a recent graduate from Rhode Island School of Design.
They predominantly work in painting, drawing, and sculpture, oftentimes thinking of ways to merge their applications into one. From
an immigrant household in the temperate suburbs of Maryland, they developed an affinity for the landscape as an image that holds command
over our ideas of personhood, origins, and material reality. Depictions of enclosed spaces and open skies come together to create
picturesque scenes that begin to dissolve out of imagination as they erode at their edges. In reinvesting in the "natural" as a visual
fallacy, they hope to meaningfully observe estrangement, idleness, and self-autonomy in an age where the marginalized find themselves
existing like ghosts in plain sight.`

export const essayContent = [
    {
        type: "paragraph",
        text: `A miserable yawn stretches itself inside from corner to corner. I used to lie in bed to this feeling, limp like an
        old comforter. Sometimes I’d imagine myself thinning till as light as a sheet and then blowing up bubble-gum style. I despised
        holding so much air and more than anything I wanted to bear the world filtering through me instead. To open my chest and bid
        them all to flood in… the birds, parks, dumps, dams, cans, cars, worms…and afterwards, I don’t know what would become of me,
        except that what was left would get to revel in the sounds of a flatulent hollow. This is wishful thinking.`,
        references: []
    },
    {
        type: "paragraph",
        text: `I am full of contradictions and one of them is that I struggle with spontaneity. A year ago, tired of this life of pause,
        I wrote a note for myself — it reads:`,
        references: []
    },
    {
        type: "blockquote",
        text:  `radicality as embodied by
        self-reliance (faith)
        change (unforeseen turns)
        and reiteration (this is becoming of ＿ and you will witness!)`
    },
    {
        type: "paragraph",
        text: `In a few short words I had laid out a sequence of to-dos in some dreamful recipe for success, happiness, peace. I dream
        of doing and I yearn to believe. How to make meaning in a world of singularity. How to signal in a sea of symbols. How we may
        move, pressed into the grooves. I've been collecting wood scraps for half a year now and routinely sort them into bins by potential
        function (spine, joint, limb, bellybutton); imagining a self between bone, plastic, wood, and stone; stingy thingy into little me, me, me...`,
        references: [{index: 539, ref: 1}]
    },
    {
        type: "paragraph",
        text: `According to Worringer, the instinct for "thing in itself" is most powerful; the yearning for abstraction comes from man's
        anxieties of the chaos that surrounds him, which brings him to believe that to "wrest the object of the external world out of its
        natural context" and shed it of all semblances of meaning, will thereby bring him relief. A form with "no smells, no noises, no
        color; no changing from one state to another and the uncertainty that comes with it." I don't find this understanding to be as
        useful in the context cesspool we live in today. Inanimate aesthetic (love) as a form of self-activation may cost us our humanity,
        but a socially-contingent livelihood as we know it is no humanity at all! (Who cares for the unsalvageable?) The marginalized
        suffer in hypervisibility and yet the persuasian of an unseen, unalivable state of being is at the heart of objectification - this
        contradiction has led us to the subject/object conundrum, ornamentalism has bloomed in its wake, and the object is now our flesh.`,
        references: [
            {index: 75, ref: 2},
            {index: 285, ref: 3},
            {index: 490, ref: 4}
        ]
    },
    {
        type: "paragraph",
        text: `Color and texture are agents of reality that manage to escape language - and thereby definition - all the time. In doing so
        they inhabit a sort of unreality, In doing so their untenability alludes to a subjectivity that does not require a form at all and signals something
        grander and ever-omnipresent. "It [is miraculous]: a miracle is always a sudden transfomration of nature...it is less a thing than a 
        trace of movement." We must conjure sense from rejection, negative meaning, association folding into association — where we are "richer
        and stranger" than we think, and hence never pure. I wonder if the answer lies in not fully knowing ourselves — nomadic and forever
        flexing instead.`,
        references: [
            {index: 111, ref: 5},
            {index:450, ref: 6},
            {index:588, ref: 7},
            {index:694, ref: 8}
        ]
    },
    {
        type: "paragraph",
        text: `Trans-fixation disturbs the pure and assemblage is an ode to brokeness. Death is the dissolution of the boundaries that define
        existence but life remains through its multitudes. I'm changing like the seasons — watch! I'll even cut off my finger. Let me break down
        and apart. Let me break down and apart.`,
        references: [{index: 253, ref: 9}]
    },
    {
        type: "paragraph",
        text: `This is not a feeling. This can be, I think, a conversation.`,
        references: [{index: 60, ref: 10}]
    }
]

export const footnotes = [
    'Cripple and the starfish, line 2',
    `Worringer, 18`,
    `Worringer, x`,
    `Batchelor, 19`,
    `Batchelor remarks, "for [him], the rainbow spreads its colour evenly at both edges but has a kink in the middle, where yellow meets green...where colour has no name" (93)`,
    `In Ornamentalism, Cheng draws upon Tony Morrison's Beloved to consider how black flesh and yellow ornament converge in racialized objectivity: "Sethe-half woman, half ornament
    , with her dead back that is also a tree — is a hybrid being whose personhood applies tremendous pressure on our notions of what constitutes living versus surviving" (30)`,
    `"The dynamics of spreading cracks may be an example of what Deleuze and Guattari call the "nomadism" of matter. Playing on the notion of metal as a conductor of electricity, 
    they say that metal "conducts" (ushers) itself through a series of self-transformations, which is not a sequential movement from one fixed point to another, but a tumbling of 
    continuous variations with fuzzy borders" (Bennett, 59)`,
    `Cripple and the Starfish, line 14 - 15`,
    `Chan, line 14`
]

export const citations = [
    `Antony and the Johnsons. "Cripple and the Starfish". 2000`,
    `Batchelor, David. Chromophobia. Reaktion Books, 2000`,
    `Barthes, Roland, Mythologies, Noonday Press, 1991`,
    `Bennett, Jane. 1957 "Vibrant Matter: a Political Ecology of Things". Duke University Press, 2010`,
    `Chan, Wo. "June 8, the smiley barist remembers my name". Poetry Foundation, Jan 2020`,
    `Cheng, Anne Anlin, "Ornamentalism: A Feminist Thoery for the Yellow Woman". Critical Inquiry vol. 44, 2018, p. 415 - 446`,
    `Worringer, Wilhelm. Abstraction and Empathy: A Contribution to the Psychology of Style. Elephant Paperbacks, 1997`
]